/**
 * @file   src\mobile\components\CreateInventory\MustHavesProperties.js
 * @brief  This file is responsible for must haves section for propertytypes other than land in create inventory
 * @date   April, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import Form from "react-bootstrap/Form";
import { capitalizeString, handleTextBoxChange } from "../../../actions/common";
import {
  checkIsNumber,
  removeDecimalZerosFromTextField,
  replaceComma,
  thousandSeperatedValue,
} from "../../../helpers/common";
import { removeNegativeInput } from "../../../helpers/validations";
import { yrMaxInValid } from "../../../constants/createWantAd";
import { useState, useEffect } from "react";
import TextBoxMob from "../../components/TextBoxMob";
import { adaAccessCheckBox, newConstructionCheckBox } from "../../../constants/common";
const MustHavesProperties = (props) => {
  const {
    isPrivateRoom,
    updateValuesToState,
    neighborHood,
    highSchool,
    noBedRm,
    noBathRm,
    sqFt,
    stories,
    lotSize,
    grgeSpots,
    yearBuilt,
    invFormError,
    errorUpdatedAt,
    adaAccess,
    IsNewConstruction,
    handleFormValues,
  } = props;

  const [yearBuiltError, setYearBuiltError] = useState("");

  /**
   * To handle neighbourhood change
   * @param {*} e
   */
  const handleNeighborhoodChange = (e) => {
    const value = capitalizeString(handleTextBoxChange(e));
    updateValuesToState("neighborHood", value);
  };
  /**
   * To hande nearest high school field change
   * @param {*} e
   */
  const handleHighSchoolChange = (e) => {
    const value = capitalizeString(handleTextBoxChange(e));
    updateValuesToState("highSchool", value);
  };
  /**
   * To handle number of bedrooms change
   * @param {*} e
   */
  const handleBedRoomChange = (e) => {
    let value = removeNegativeInput(handleTextBoxChange(e));
    if (replaceComma(value) > 100) {
      value = 100;
    }
    updateValuesToState("noBedRm", replaceComma(value));
  };
  /**
   * To handle number of bathroom change
   * @param {*} e
   */
  const handleBathRoomChange = (e) => {
    let value = removeNegativeInput(handleTextBoxChange(e));
    if (replaceComma(value) > 100) {
      value = 100;
    }
    updateValuesToState("noBathRm", replaceComma(value));
  };

  /**
   * To handle sqft change
   * @param {*} e
   */
  const handleSqFtChange = (e) => {
    let value = removeNegativeInput(handleTextBoxChange(e));
    updateValuesToState("sqFt", replaceComma(value));
  };
  /**
   * To handle storey change
   * @param {*} e
   */
  const handleStoryChange = (e) => {
    const value = removeNegativeInput(handleTextBoxChange(e));
    updateValuesToState("stories", replaceComma(value));
  };
  /***
   * To handle lot size change
   */
  const handleLotSizeChange = (e) => {
    const value = removeNegativeInput(handleTextBoxChange(e));
    updateValuesToState("lotSize", replaceComma(value));
  };
  /**
   * Ti handle changes in garage spots field
   * @param {*} e
   */
  const handleGarrageSpotChange = (e) => {
    const value = removeNegativeInput(handleTextBoxChange(e));
    updateValuesToState("grgeSpots", replaceComma(value));
  };

  /**
   * To handle changes in year built field
   * @param {*} e
   */
  const handleYearBuitChange = (e) => {
    const value = removeNegativeInput(handleTextBoxChange(e));
    if (checkIsNumber(value)) {
      updateValuesToState("yearBuilt", value);
    }
  };
  /**
   * To handle new construction checkbox
   * @param {*} e
   */
  const handleCheckBoxClick = (e) => {
    if (e.target.id === adaAccessCheckBox) {
      handleFormValues({ adaAccess: e.target.checked });
    } else {
      handleFormValues({ IsNewConstruction: e.target.checked });
    }
  };

  useEffect(() => {
    setYearBuiltError("");

    if (invFormError) {
      switch (invFormError) {
        case yrMaxInValid:
          setYearBuiltError("Enter Valid year");
          break;
      }
    }
  }, [invFormError, errorUpdatedAt]);

  return (
    <>
      <div className="pt-20">
        <p className="mt-0 mb-2 fnt12">DETAILS</p>
        <h2 className="mt-0 pt-0 mb-2 titleText">
          Does this information look right?
        </h2>
        <p className="fnt16">
          If anything doesn’t look accurate, update it with the correct
          information.
        </p>
      </div>

      {!isPrivateRoom ? (
        <>
          <div className="row">
            <div className="col-12">
              <Form.Group
                className="mb-3"
                controlId="exampleForm.ControlInput1"
              >
                <Form.Label className="fnt14">Neighborhood</Form.Label>
                <Form.Control
                  type="text"
                  onChange={(e) => {
                    handleNeighborhoodChange(e);
                  }}
                  value={neighborHood}
                />
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <Form.Group
                className="mb-3"
                controlId="exampleForm.ControlInput1"
              >
                <Form.Label className="fnt14">High School</Form.Label>
                <Form.Control
                  type="text"
                  value={highSchool}
                  onChange={(e) => {
                    handleHighSchoolChange(e);
                  }}
                />
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-6">
              <Form.Group
                className="mb-3"
                controlId="exampleForm.ControlInput1"
              >
                <Form.Label className="fnt14">
                  Bedrooms<span>*</span>
                </Form.Label>
                <Form.Control
                  pattern="[0-9]*"
                  inputmode="numeric"
                  className="form-control"
                  value={
                    noBedRm
                      ? thousandSeperatedValue(
                          removeDecimalZerosFromTextField(noBedRm)
                        )
                      : ""
                  }
                  onChange={(e) => {
                    handleBedRoomChange(e);
                  }}
                />
              </Form.Group>
            </div>
            <div className="col-6">
              <Form.Group
                className="mb-3"
                controlId="exampleForm.ControlInput1"
              >
                <Form.Label className="fnt14">
                  Bathrooms<span>*</span>
                </Form.Label>
                <Form.Control
                  pattern="[0-9]*"
                  inputmode="numeric"
                  className="form-control"
                  value={
                    noBathRm
                      ? thousandSeperatedValue(
                          removeDecimalZerosFromTextField(noBathRm)
                        )
                      : ""
                  }
                  onChange={(e) => {
                    handleBathRoomChange(e);
                  }}
                />
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-6">
              <Form.Group
                className="mb-3"
                controlId="exampleForm.ControlInput1"
              >
                <Form.Label className="fnt14">
                  Square Footage<span>*</span>
                </Form.Label>
                <Form.Control
                  pattern="[0-9]*"
                  inputmode="numeric"
                  className="form-control"
                  value={
                    sqFt
                      ? thousandSeperatedValue(
                          removeDecimalZerosFromTextField(sqFt)
                        )
                      : ""
                  }
                  onChange={(e) => {
                    handleSqFtChange(e);
                  }}
                />
              </Form.Group>
            </div>
            <div className="col-6">
              <Form.Group
                className="mb-3"
                controlId="exampleForm.ControlInput1"
              >
                <Form.Label className="fnt14">Stories</Form.Label>
                <Form.Control
                  inputmode="numeric"
                  className="form-control"
                  value={thousandSeperatedValue(stories) || ""}
                  onChange={(e) => {
                    handleStoryChange(e);
                  }}
                />
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-6">
              <Form.Group
                className="mb-3"
                controlId="exampleForm.ControlInput1"
              >
                <Form.Label className="fnt14">Lot Size (Acres)</Form.Label>
                <Form.Control
                  pattern="[0-9.]*"
                  inputmode="numeric"
                  className="form-control"
                  value={
                    lotSize
                      ? thousandSeperatedValue(
                          removeDecimalZerosFromTextField(lotSize)
                        )
                      : ""
                  }
                  onChange={(e) => {
                    handleLotSizeChange(e);
                  }}
                />
              </Form.Group>
            </div>
            <div className="col-6">
              <Form.Group
                className="mb-3"
                controlId="exampleForm.ControlInput1"
              >
                <Form.Label className="fnt14">Garage Spaces</Form.Label>
                <Form.Control
                  pattern="[0-9]*"
                  inputmode="numeric"
                  className="form-control"
                  value={thousandSeperatedValue(grgeSpots) || ""}
                  onChange={(e) => {
                    handleGarrageSpotChange(e);
                  }}
                />
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-6">
              <Form.Group
                className="mb-3"
                controlId="exampleForm.ControlInput1"
              >
                <Form.Label className="fnt14">Year Built</Form.Label>
                <TextBoxMob
                  pattern="[0-9]*"
                  inputmode="numeric"
                  className="form-control"
                  errorMessage={yearBuiltError}
                  value={yearBuilt}
                  onChange={(e) => {
                    handleYearBuitChange(e);
                  }}
                />
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-6 pe-2 pb-3">
              <label
                className="form-check-label checkbox-label"
                for={newConstructionCheckBox}
              >
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={newConstructionCheckBox}
                  checked={IsNewConstruction}
                  onChange={(e) => handleCheckBoxClick(e)}
                />
                <span className="fnt14"> New Construction</span>
              </label>
            </div>
            <div className="col-6 ps-2 pb-3">
              <label
                className="form-check-label checkbox-label"
                for={adaAccessCheckBox}
              >
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={adaAccessCheckBox}
                  checked={adaAccess}
                  onChange={(e) => handleCheckBoxClick(e)}
                />
                <span className="fnt14"> ADA Accessible</span>
              </label>
            </div>
          </div>
        </>
      ) : (
        <div className="col-6 ps-2 pb-3">
          <label
            className="form-check-label checkbox-label"
            for={adaAccessCheckBox}
          >
            <input
              className="form-check-input"
              type="checkbox"
              id={adaAccessCheckBox}
              checked={adaAccess}
              onChange={(e) => handleCheckBoxClick(e)}
            />
            <span className="fnt14"> ADA Accessible</span>
          </label>
        </div>
      )}
    </>
  );
};

export default MustHavesProperties;
